// Personal read projections never come from account-wide conversation events.
const READ_FIELDS = [
  'agent_last_seen_at',
  'assignee_last_seen_at',
  'unread_count',
];
const isReadField = (key) =>
  READ_FIELDS.includes(key) || key.startsWith('personal_');

export const loadedMessageIds = (messages) => [
  ...new Set(
    (messages || [])
      .map((message) => message.id)
      .filter((id) => Number.isSafeInteger(id) && id > 0),
  ),
];

export const isPersonalReadViewActive = ({
  conversationId,
  selectedId,
  accountId,
  currentAccountId,
}) =>
  Number(conversationId) === Number(selectedId) &&
  accountId === currentAccountId &&
  document.visibilityState === 'visible' &&
  document.hasFocus();

export const isNewerPersonalRead = (incoming, existing = {}) => {
  if (!existing.personal_read_user_id) return true;
  if (incoming.personal_read_user_id !== existing.personal_read_user_id)
    return false;
  for (const key of [
    'personal_read_generation',
    'personal_read_revision',
    'personal_read_snapshot_at',
  ]) {
    const difference = Number(incoming[key] || 0) - Number(existing[key] || 0);
    if (difference !== 0) return difference > 0;
  }
  return true;
};

export const mergePersonalRead = (incoming, existing = {}) => {
  if (!incoming.personal_read_enabled && !existing.personal_read_enabled)
    return incoming;
  if (incoming.personal_read_enabled === false) return incoming;
  const result = { ...incoming };
  Object.keys(result)
    .filter(isReadField)
    .forEach((key) => delete result[key]);
  Object.keys(existing)
    .filter(isReadField)
    .forEach((key) => {
      result[key] = existing[key];
    });
  result.personal_read_enabled = true;
  if (!incoming.personal_read_user_id) return result;
  const summary = incoming.list_summary === true;
  const sameUser = incoming.personal_read_user_id === existing.personal_read_user_id;
  const sameGeneration = sameUser &&
    incoming.personal_read_generation === existing.personal_read_generation;
  const sameVersion = sameGeneration &&
    incoming.personal_read_revision === existing.personal_read_revision;
  const newer = isNewerPersonalRead(incoming, existing);
  // A list has no observed-message details. Its newer aggregate snapshot must
  // not discard an in-flight detailed response for the same read version.
  const detailClock = existing.personal_read_detail_snapshot_at ??
    (existing.list_summary ? 0 : existing.personal_read_snapshot_at || 0);
  const compatibleDetail = !summary && sameVersion &&
    Number(incoming.personal_read_snapshot_at || 0) >= Number(detailClock);
  if (!newer && !compatibleDetail) return result;
  if (newer) {
    Object.keys(incoming).filter(isReadField).forEach((key) => {
      result[key] = incoming[key];
    });
  }
  if (summary) {
    result.personal_read_detail_snapshot_at = sameVersion ? detailClock : 0;
    if (!sameGeneration) {
      result.personal_unread_message_ids = [];
      result.personal_observed_message_ids = [];
    }
    return result;
  }
  result.personal_read_detail_snapshot_at = incoming.personal_read_snapshot_at || 0;
  result.personal_observed_message_ids = incoming.personal_observed_message_ids || [];
  // The total covers the whole conversation even when a batch response only
  // echoes a subset of the observed message IDs.
  if (newer && incoming.unread_count === 0) {
    result.personal_unread_message_ids = [];
    return result;
  }
  const unread = new Set(
    sameGeneration ? existing.personal_unread_message_ids || [] : [],
  );
  (incoming.personal_observed_message_ids || []).forEach((id) =>
    unread.delete(id),
  );
  (incoming.personal_unread_message_ids || []).forEach((id) => unread.add(id));
  result.personal_unread_message_ids = [...unread];
  return result;
};

export const personalUnreadMessages = (messages, conversation) => {
  const unread = new Set(conversation.personal_unread_message_ids || []);
  return (messages || []).filter((message) => unread.has(message.id));
};
